import {
  useState,
  ReactNode,
  Fragment,
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import Paper from '@mui/material/Paper'
import {
  TableCell,
  TableContainer,
  TablePagination,
  Collapse,
  IconButton,
  CircularProgress,
  Box,
  TableRow,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  FilterAlt,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import { useTheme } from '@mui/material/styles'
import { TableColumn } from 'types/types'
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableCellContent,
  StyledTableRow,
} from './Table.ui'
import { TableHeader } from './TableHeader'
import { TableHeaderType } from './types'
import {
  TFiltersState,
  TFilterTypes,
  TSortState,
  TSortValue,
} from './TableFilter/types'
import {
  createFilterState,
  createSortState,
  handleIsFilter,
} from './TableFilter/utils'
import { Reset } from './TableFilter/constants'
import * as sxStyles from './table.style'
import { EmptyState } from '../ui/EmptyState/EmptyState'
import { useSelector, useDispatch } from 'react-redux'
import { setTablePagination } from '../../store/slices/tableSlice'
import { RootState } from '../../store'

interface TableProps<T> {
  columns: TableColumn[]
  data: T[]
  total?: number
  isLoading?: boolean
  noData?: boolean
  showHeader?: boolean
  showPagination?: boolean
  renderExpandableRow?: (row: T) => ReactNode
  headerType?: TableHeaderType
  onRowClick?: (row: T) => void
  showExpandableRowAtRight?: boolean
  onPaginationChange?: (page: number, perPage: number) => void
  handleGlobalFilter?: (filters: TFiltersState, sort?: TSortState) => void
  handleGlobalSort?: (sort: TSortState, filters?: TFiltersState) => void
  isScrollX?: boolean
  tableKey?: string
  displayShowAll?: boolean
}

export const Table = <T extends { id: string | number }>({
  columns,
  data,
  showHeader = true,
  showPagination = false,
  renderExpandableRow,
  headerType,
  isLoading,
  total,
  onPaginationChange,
  onRowClick,
  handleGlobalFilter,
  handleGlobalSort,
  showExpandableRowAtRight,
  isScrollX,
  tableKey,
  displayShowAll,
}: TableProps<T>) => {
  const dispatch = useDispatch()
  const currentCompany = useSelector(
    (state: RootState) => state?.user?.currentCompany
  )
  const companyId = currentCompany?.id
  const scopedTableKey =
    tableKey && companyId ? `${tableKey}_${companyId}` : undefined

  const paginationState = useSelector((state: any) =>
    scopedTableKey ? state.table?.[scopedTableKey] : undefined
  )

  useEffect(() => {
    if (scopedTableKey && !paginationState) {
      dispatch(
        setTablePagination({
          tableKey: scopedTableKey,
          page: 0,
          rowsPerPage: 0,
        })
      )
    }
  }, [scopedTableKey])

  const [page, setPage] = useState(() =>
    scopedTableKey ? paginationState?.page || 0 : 0
  )
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    scopedTableKey ? paginationState?.rowsPerPage || 25 : 25
  )
  const [openRows, setOpenRows] = useState<Record<string | number, boolean>>({})

  const [filterState, setFilterState] = useState(() =>
    createFilterState(columns)
  )

  const prevColumnIds = useRef(columns.map((col) => col.id))

  useEffect(() => {
    const currentColumnIds = columns.map((col) => col.id)
    const hasNewColumns = currentColumnIds.some(
      (id) => !prevColumnIds.current.includes(id)
    )

    if (hasNewColumns) {
      const newFilterState = createFilterState(columns)
      setFilterState((prevState) => {
        const mergedState = { ...newFilterState }
        Object.keys(prevState).forEach((key) => {
          if (currentColumnIds.includes(key)) {
            mergedState[key] = prevState[key]
          }
        })
        return mergedState
      })

      prevColumnIds.current = currentColumnIds
    }
  }, [columns])

  const [sortState, setSortState] = useState<TSortState>(
    createSortState(columns)
  )

  const isFilter = handleIsFilter(columns)

  const handleFilterState = (
    columnId: string,
    type: TFilterTypes,
    value: string
  ) => {
    const newState = { ...filterState }
    if (!newState[columnId]) {
      return
    }
    if (type === Reset) {
      newState[columnId].filterType = ''
      newState[columnId].value = ''
      setFilterState(newState)
      return
    }

    newState[columnId].value = value

    if (type === 'between' && !Array.isArray(value)) {
      newState[columnId].value = ''
    }

    if (type !== 'between' && Array.isArray(value)) {
      newState[columnId].value = ''
    }

    if (type) newState[columnId].filterType = type

    setFilterState(newState)
  }

  const handleSortState = (columnId: string, sort: TSortValue) => {
    const newState = { ...sortState }
    newState[columnId] = sort
    setSortState(newState)

    handleGlobalSort?.(newState, filterState)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    if (scopedTableKey) {
      dispatch(
        setTablePagination({
          tableKey: scopedTableKey,
          page: newPage,
          rowsPerPage,
        })
      )
    }
    onPaginationChange?.(newPage, rowsPerPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    if (scopedTableKey) {
      dispatch(
        setTablePagination({
          tableKey: scopedTableKey,
          page: 0,
          rowsPerPage: newRowsPerPage,
        })
      )
    }
    onPaginationChange?.(0, newRowsPerPage)
  }

  const toggleRow = (id: string | number) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const theme = useTheme()

  const handleApplyGlobalFilters = useCallback(() => {
    setRowsPerPage(25)
    setPage(0)
    handleGlobalFilter?.(filterState, sortState)
    if (scopedTableKey) {
      dispatch(
        setTablePagination({
          tableKey: scopedTableKey,
          page: 0,
          rowsPerPage: 25,
        })
      )
    }
  }, [filterState, sortState, handleGlobalFilter, dispatch])

  // CheckBox state
  const [showAllSelected, setShowAllSelected] = useState<boolean>(false)
  const clearFilter = () => {
    const emptyFilterState = {}
    columns.forEach((column) => {
      if (column.id) {
        emptyFilterState[column.id] = {
          ...filterState[column.id],
          value: '',
          filterField: column.filterParams?.filterField || column.id,
          filterOptions: column.filterParams?.filterOptions || [],
        }
      }
    })

    setShowAllSelected(true)
    setFilterState(emptyFilterState)

    if (handleGlobalFilter) {
      handleGlobalFilter(emptyFilterState, sortState)
    }
  }

  const defaultFiltersAppliedRef = useRef(false)

  useEffect(() => {
    if (!defaultFiltersAppliedRef.current && handleGlobalFilter) {
      const hasDefaultFilters = columns.some(
        (column) =>
          column.filterParams &&
          column.filterParams.defaultFilterValue &&
          column.filterParams.defaultOperatorValue
      )

      if (hasDefaultFilters) {
        const initialFilterState = { ...filterState }

        columns.forEach((column) => {
          if (
            column.id &&
            column.filterParams &&
            column.filterParams.defaultFilterValue !== undefined &&
            column.filterParams.defaultOperatorValue
          ) {
            initialFilterState[column.id] = {
              ...initialFilterState[column.id],
              filterType: column.filterParams.defaultOperatorValue,
              value: column.filterParams.defaultFilterValue,
              filterField: column.filterParams.filterField,
            }
          }
        })

        setFilterState(initialFilterState)

        handleGlobalFilter(initialFilterState, sortState)

        defaultFiltersAppliedRef.current = true
      }
    }
  }, [columns, handleGlobalFilter])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleApplyGlobalFilters()
    }
  }

  useEffect(() => {
    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleApplyGlobalFilters()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [filterState, sortState])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!data || data.length < 1) {
    return (
      <Paper
        sx={{
          ...sxStyles.paperStyles,
          display: 'flex',
          flexDirection: 'column',
        }}
        onKeyDown={handleKeyDown}
      >
        {isFilter ? (
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <Box sx={sxStyles.filterActionsContainer}>
              Filter:
              <IconButton onClick={handleApplyGlobalFilters}>
                <FilterAlt color="info" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Clear:
              <IconButton
                onClick={() => {
                  clearFilter()
                }}
              >
                <FilterAltOffIcon color="error" />
              </IconButton>
            </Box>
          </Box>
        ) : null}
        <TableContainer
          sx={{
            minHeight: 0,
            width: '100%',
          }}
        >
          <StyledTable
            stickyHeader
            aria-label="sticky table"
            sx={{
              ...(isScrollX && {
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }),
            }}
          >
            {showHeader && (
              <TableHeader
                columns={columns}
                hasExpendableRow={!!renderExpandableRow}
                filterState={filterState}
                handleFilterState={handleFilterState}
                sortState={sortState}
                setSortState={setSortState}
                handleSortState={handleSortState}
                handleApplyGlobalFilters={handleApplyGlobalFilters}
              />
            )}
            <tbody style={{ height: '100%' }}>
              <TableRow sx={{ height: '100%' }}>
                <TableCell
                  colSpan={columns.length + (renderExpandableRow ? 1 : 0)}
                  sx={{ height: '100%', textAlign: 'center', border: 'none' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <EmptyState />
                  </Box>
                </TableCell>
              </TableRow>
            </tbody>
          </StyledTable>
        </TableContainer>
      </Paper>
    )
  }

  return (
    <Paper
      sx={{
        ...sxStyles.paperStyles,
        display: 'flex',
        flexDirection: 'column',
      }}
      onKeyDown={handleKeyDown}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          pl: 2,
          flexShrink: 0,
        }}
      >
        {displayShowAll ? (
          <Box>
            <FormControlLabel
              control={<Checkbox />}
              label="Show All"
              checked={showAllSelected}
              onChange={(_selected, checked) => {
                if (checked) {
                  clearFilter()
                } else {
                  setFilterState(createFilterState(columns))
                  handleGlobalFilter?.(createFilterState(columns), sortState)
                  setShowAllSelected(false)
                }
              }}
            />
          </Box>
        ) : null}
        {isFilter ? (
          <Box sx={{ display: 'flex', flex: 1 }}>
            <Box sx={sxStyles.filterActionsContainer}>
              Filter:
              <IconButton onClick={handleApplyGlobalFilters}>
                <FilterAlt color="info" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Clear:
              <IconButton
                onClick={() => {
                  clearFilter()
                }}
              >
                <FilterAltOffIcon color="error" />
              </IconButton>
            </Box>
          </Box>
        ) : null}
      </Stack>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: isScrollX ? 'auto' : 'hidden',
        }}
      >
        <StyledTable
          stickyHeader
          aria-label="sticky table"
          sx={{
            ...(isScrollX && {
              whiteSpace: 'nowrap',
              minWidth: 'max-content',
            }),
          }}
        >
          {showHeader && (
            <TableHeader
              columns={columns}
              hasExpendableRow={!!renderExpandableRow}
              filterState={filterState}
              handleFilterState={handleFilterState}
              sortState={sortState}
              setSortState={setSortState}
              handleSortState={handleSortState}
              handleApplyGlobalFilters={handleApplyGlobalFilters}
            />
          )}
          <StyledTableBody headerType={headerType}>
            {data?.map((row) => {
              const isOpen = openRows[row.id]
              return (
                <Fragment key={row.id}>
                  <StyledTableRow
                    onClick={() => onRowClick?.(row)}
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {columns.map((column, index) => {
                      const value = row[column.id as keyof T]
                      const expandableRow = (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleRow(row.id)
                          }}
                          sx={{
                            bgcolor: isOpen
                              ? theme.palette.primary.main
                              : 'white',
                            color: isOpen ? 'white' : 'unset',
                            '&:hover': {
                              opacity: 0.8,
                              bgcolor: isOpen
                                ? theme.palette.primary.main
                                : 'white',
                            },
                          }}
                        >
                          {isOpen ? (
                            <KeyboardArrowUp
                              sx={{
                                fontSize: 14,
                              }}
                            />
                          ) : (
                            <KeyboardArrowDown
                              sx={{
                                fontSize: 14,
                              }}
                            />
                          )}
                        </IconButton>
                      )

                      return (
                        <StyledTableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            position: column.isStickyLeft
                              ? 'sticky'
                              : column.isStickyRight
                                ? 'sticky'
                                : undefined,
                            left: column.isStickyLeft ? 0 : undefined,
                            right: column.isStickyRight ? 0 : undefined,
                            zIndex:
                              column.isStickyLeft || column.isStickyRight
                                ? 1
                                : undefined,
                            minWidth: column.minWidth,
                            width: column.minWidth,
                            maxWidth: column?.minWidth,
                          }}
                        >
                          <StyledTableCellContent
                            align={column?.align}
                            sx={{
                              opacity:
                                (row as any).is_active === false ? 0.6 : 1,
                            }}
                          >
                            {renderExpandableRow &&
                              !showExpandableRowAtRight &&
                              index === 0 &&
                              expandableRow}
                            <Box sx={sxStyles.columnValueContainer}>
                              {column.format
                                ? column.format(value, row)
                                : (value as ReactNode)}
                            </Box>
                            {renderExpandableRow &&
                              showExpandableRowAtRight &&
                              index === columns.length - 1 && (
                                <Box sx={sxStyles.expandableRowContainer}>
                                  {expandableRow}
                                </Box>
                              )}
                          </StyledTableCellContent>
                        </StyledTableCell>
                      )
                    })}
                  </StyledTableRow>
                  {renderExpandableRow && isOpen && (
                    <StyledTableRow>
                      <TableCell
                        sx={sxStyles.expandableRowCell}
                        colSpan={columns.length}
                      >
                        <Box
                          px="16px"
                          sx={{
                            borderBottomLeftRadius: '10px',
                            borderBottomRightRadius: '10px',
                            background: theme.palette.grey['100'],
                          }}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            {renderExpandableRow(row)}
                          </Collapse>
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  )}
                </Fragment>
              )
            })}
          </StyledTableBody>
        </StyledTable>
      </TableContainer>

      {showPagination && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={total ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </Paper>
  )
}
